import { useMemo } from "react";
import { useServerFn } from "@tanstack/react-start";
import { synthesizeSpeech } from "@/lib/tts.functions";
import { getStoredAccessToken } from "@/lib/auth-session";
import { splitLocalePath } from "@/lib/locale";

/**
 * The language the member is currently reading, taken from the address bar.
 * Spoken guidance follows it automatically — no separate voice setting.
 */
export function currentVoiceLang(): string {
  if (typeof window === "undefined") return "en";
  try {
    return splitLocalePath(window.location.pathname).locale;
  } catch {
    return "en";
  }
}

export type VoicePref = "female" | "male" | "neutral" | "whisper";

export const VOICE_IDS: Record<VoicePref, string> = {
  female: "2qfp6zPuviqeCOZIE9RZ", // Female
  // Male voice retired — any saved "male" preference now uses the female voice.
  male: "2qfp6zPuviqeCOZIE9RZ",
  neutral: "2qfp6zPuviqeCOZIE9RZ",
  whisper: "xsfXCyY98Gbq4cV85MC1", // Gentle Gemma (whispering)
};

export function voiceIdFor(pref: VoicePref): string {
  return VOICE_IDS[pref] ?? VOICE_IDS.female;
}

/**
 * TTS caller — the bound result of `useServerFn(synthesizeSpeech)`.
 * Must be produced inside a React component via `useTts()` (or `useServerFn`
 * directly) so the TanStack Start router middleware attaches the auth bearer
 * token to the RPC call. Calling `synthesizeSpeech` directly via module import
 * bypasses the middleware and stalls in the current build.
 */
export type TtsCaller = ReturnType<typeof useServerFn<typeof synthesizeSpeech>>;

/**
 * LRU cache for synthesized speech.
 *
 * Keyed by `${voiceId}::${text}`. Same script line + same voice returns the
 * cached `data:audio/*;base64,…` URL immediately without another server
 * call. Bounded by both entry count and total base64 byte size so the
 * cache stays small in memory even across long sessions — least recently
 * used entries fall off first.
 */
const MAX_ENTRIES = 60;
const MAX_BYTES = 12 * 1024 * 1024; // ~12 MB of base64 payload
const SILENT_WAV_DATA_URL =
  "data:audio/wav;base64,UklGRiQAAABXQVZFZm10IBAAAAABAAEAgD4AAAB9AAACABAAZGF0YQAAAAA=";

type CacheEntry = { url: string; bytes: number };
const cache = new Map<string, CacheEntry>();
let cacheBytes = 0;
const cacheStats = { hits: 0, misses: 0 };

function cacheGet(key: string): string | undefined {
  const entry = cache.get(key);
  if (!entry) {
    cacheStats.misses += 1;
    return undefined;
  }
  // Bump to most-recent by re-inserting (Map keeps insertion order).
  cache.delete(key);
  cache.set(key, entry);
  cacheStats.hits += 1;
  return entry.url;
}

function cacheSet(key: string, url: string) {
  const bytes = url.length;
  const existing = cache.get(key);
  if (existing) {
    cacheBytes -= existing.bytes;
    if (existing.url.startsWith("blob:")) URL.revokeObjectURL(existing.url);
    cache.delete(key);
  }
  cache.set(key, { url, bytes });
  cacheBytes += bytes;
  // Evict LRU entries until under both caps.
  while (
    (cache.size > MAX_ENTRIES || cacheBytes > MAX_BYTES) &&
    cache.size > 0
  ) {
    const oldestKey = cache.keys().next().value as string | undefined;
    if (!oldestKey) break;
    const oldest = cache.get(oldestKey)!;
    cacheBytes -= oldest.bytes;
    if (oldest.url.startsWith("blob:")) URL.revokeObjectURL(oldest.url);
    cache.delete(oldestKey);
  }
}

export function getTtsCacheStats() {
  return {
    entries: cache.size,
    bytes: cacheBytes,
    hits: cacheStats.hits,
    misses: cacheStats.misses,
  };
}

export function clearTtsCache() {
  cache.forEach((entry) => {
    if (entry.url.startsWith("blob:")) URL.revokeObjectURL(entry.url);
  });
  cache.clear();
  cacheBytes = 0;
  cacheStats.hits = 0;
  cacheStats.misses = 0;
}

const inflight = new Map<string, Promise<string>>();

/** True if `text` in `voiceId` is already in the LRU cache (no network needed). */
export function hasCachedTts(text: string, voiceId: string): boolean {
  return cache.has(keyFor(voiceId, text));
}

/** True if a fill for this key is currently in flight (network is warming). */
export function isPrefetchingTts(text: string, voiceId: string): boolean {
  return inflight.has(keyFor(voiceId, text));
}

/**
 * Adaptive prefetch window.
 *
 * The player asks `getPrefetchWindow()` how many upcoming segments to warm
 * beyond the one currently playing. The window auto-tunes from two signals:
 *   - observed synthesis latency (EWMA of server round-trips on cache misses)
 *   - effective segment duration at the current playback speed
 * The heuristic keeps enough segments in flight that a fresh synth can
 * complete before the currently-playing clip ends, clamped to [min, max].
 * A manual override wins over the heuristic when set.
 */
let prefetchMin = 1;
let prefetchMax = 3;
let manualWindow: number | null = null;
let avgSynthMs = 1200; // EWMA seed — updated on every cache-miss synth
let avgSegmentMs = 8000; // EWMA seed — updated when audio.duration is known
let playbackSpeed = 1;

const EWMA_ALPHA = 0.3;
function ewma(prev: number, sample: number) {
  return prev * (1 - EWMA_ALPHA) + sample * EWMA_ALPHA;
}

export function configurePrefetchWindow(opts: {
  min?: number;
  max?: number;
  manual?: number | null;
}) {
  if (opts.min != null) prefetchMin = Math.max(1, Math.floor(opts.min));
  if (opts.max != null) prefetchMax = Math.max(prefetchMin, Math.floor(opts.max));
  if (opts.manual !== undefined) manualWindow = opts.manual;
}

export function setPrefetchPlaybackSpeed(speed: number) {
  if (Number.isFinite(speed) && speed > 0) playbackSpeed = speed;
}

export function recordSynthesisTime(ms: number) {
  if (Number.isFinite(ms) && ms > 0) avgSynthMs = ewma(avgSynthMs, ms);
}

export function recordSegmentDuration(ms: number) {
  if (Number.isFinite(ms) && ms > 500) avgSegmentMs = ewma(avgSegmentMs, ms);
}

export function getPrefetchWindow(): number {
  if (manualWindow != null) {
    return Math.min(prefetchMax, Math.max(prefetchMin, manualWindow));
  }
  const effectiveSegmentMs = avgSegmentMs / Math.max(0.25, playbackSpeed);
  // Enough lookahead so a fresh synth finishes before the current clip ends,
  // rounded up, with a floor of 1 so we always warm at least the next one.
  const raw = Math.ceil(avgSynthMs / Math.max(500, effectiveSegmentMs));
  return Math.min(prefetchMax, Math.max(prefetchMin, raw));
}

export function getPrefetchStats() {
  return {
    avgSynthMs,
    avgSegmentMs,
    playbackSpeed,
    min: prefetchMin,
    max: prefetchMax,
    manualWindow,
    window: getPrefetchWindow(),
  };
}
let currentAudio: HTMLAudioElement | null = null;

type LastRequest = {
  text: string;
  voiceId: string;
  play: boolean;
  opts?: PlayOpts;
};
let lastRequest: LastRequest | null = null;

export type TtsErrorInfo = {
  message: string;
  error: unknown;
  retry: (tts: TtsCaller) => void;
};
type ErrorListener = (info: TtsErrorInfo) => void;
const errorListeners = new Set<ErrorListener>();

export function onTtsError(listener: ErrorListener): () => void {
  errorListeners.add(listener);
  return () => errorListeners.delete(listener);
}

function friendlyMessage(err: unknown): string {
  const raw = err instanceof Error ? err.message : String(err ?? "");
  if (/unauthor|401|403/i.test(raw)) return "Voice unavailable — please sign in again.";
  if (/network|fetch|timeout|ECONN/i.test(raw)) return "Voice couldn't load. Check your connection.";
  if (/no audio|empty/i.test(raw)) return "Voice returned no audio. Please try again.";
  return "Voice couldn't play right now.";
}

function emitError(err: unknown) {
  const info: TtsErrorInfo = {
    message: friendlyMessage(err),
    error: err,
    retry: (tts: TtsCaller) => retryLastTts(tts),
  };
  errorListeners.forEach((l) => {
    try { l(info); } catch {}
  });
}

function keyFor(voiceId: string, text: string) {
  // Language is part of the key so switching language never replays the
  // previously cached English audio.
  return `${currentVoiceLang()}::${voiceId}::${text}`;
}

/**
 * Set of "waiter" resolvers per key. Each waiter is one caller of
 * `prefetchTts` currently awaiting the shared underlying network fill.
 * Aborting a waiter rejects only that awaiter — the underlying request
 * continues so the cache still populates for the eventual real playback.
 */
type Waiter = {
  resolve: (url: string) => void;
  reject: (err: unknown) => void;
};
const waiters = new Map<string, Set<Waiter>>();
/**
 * Debug instrumentation.
 *
 * Enabled automatically in Vite dev, or on any environment when the user
 * sets `localStorage.tts_debug = "1"` (survives a preview reload). Emits
 * a compact ring buffer of prefetch/playback events keyed by a short hash
 * of the cache key, so start/finish pairs and segment changes correlate
 * visibly in the console. Consumers can also subscribe via `onTtsDebug`
 * to render a live overlay (see the audio health page).
 */
export type TtsDebugEvent =
  | { type: "prefetch:start"; keyHash: string; textPreview: string; t: number }
  | { type: "prefetch:finish"; keyHash: string; ms: number; bytes: number; t: number }
  | { type: "prefetch:error"; keyHash: string; ms: number; message: string; t: number }
  | { type: "prefetch:hit"; keyHash: string; textPreview: string; t: number }
  | { type: "prefetch:abort"; keyHash: string; t: number }
  | { type: "segment:change"; route: string; from: number | null; to: number; textPreview: string; t: number }
  | { type: "segment:play"; route: string; idx: number; cached: boolean; t: number }
  | { type: "segment:end"; route: string; idx: number; durationMs: number; t: number };

const DEBUG_MAX = 200;
const debugBuffer: TtsDebugEvent[] = [];
type DebugListener = (evt: TtsDebugEvent) => void;
const debugListeners = new Set<DebugListener>();

function debugEnabled(): boolean {
  try {
    if (typeof window !== "undefined" && window.localStorage?.getItem("tts_debug") === "1") {
      return true;
    }
  } catch {}
  try {
    return Boolean((import.meta as { env?: { DEV?: boolean } }).env?.DEV);
  } catch {
    return false;
  }
}

function shortHash(input: string): string {
  let h = 5381;
  for (let i = 0; i < input.length; i++) h = ((h << 5) + h + input.charCodeAt(i)) | 0;
  return (h >>> 0).toString(36).slice(0, 6);
}

function preview(text: string, n = 40): string {
  const clean = text.replace(/\s+/g, " ").trim();
  return clean.length > n ? clean.slice(0, n - 1) + "…" : clean;
}

function emitDebug(evt: TtsDebugEvent) {
  debugBuffer.push(evt);
  if (debugBuffer.length > DEBUG_MAX) debugBuffer.shift();
  debugListeners.forEach((l) => {
    try { l(evt); } catch {}
  });
  if (debugEnabled() && typeof console !== "undefined") {
    // Compact one-line log so the console reads as a timeline.
    const tag = `[tts ${evt.type}]`;
    console.debug(tag, evt);
  }
}

export function onTtsDebug(listener: DebugListener): () => void {
  debugListeners.add(listener);
  return () => debugListeners.delete(listener);
}

export function getTtsDebugLog(): TtsDebugEvent[] {
  return debugBuffer.slice();
}

export function clearTtsDebugLog() {
  debugBuffer.length = 0;
}

/**
 * Correlation report over the last `n` played segments.
 *
 * Walks the debug ring buffer and, for each `segment:play`, correlates it
 * back to the most recent `prefetch:finish` / `prefetch:hit` for the same
 * `keyHash`. This surfaces three numbers the overlay can render as a
 * one-line health summary:
 *   - avgSynthMs — mean of `prefetch:finish.ms` in the window
 *   - hitRate    — cache-served plays ÷ total plays (0..1)
 *   - avgReadyGapMs — mean of (play.t − prefetch-ready.t); positive means
 *     the fill landed before playback, negative means the segment waited
 *     for the network. Cold plays (no prefetch at all) count as gap 0
 *     and increment `coldPlays` separately.
 */
export type TtsCorrelationReport = {
  window: number;
  plays: number;
  cachedPlays: number;
  coldPlays: number;
  hitRate: number;
  avgSynthMs: number | null;
  synthSamples: number;
  avgReadyGapMs: number | null;
  readySamples: number;
};

export function getTtsCorrelationReport(n = 20): TtsCorrelationReport {
  const buf = debugBuffer;
  // Collect the last n segment:play events, oldest→newest.
  const plays: Array<Extract<TtsDebugEvent, { type: "segment:play" }> & { keyHash?: string }> = [];
  for (let i = buf.length - 1; i >= 0 && plays.length < n; i--) {
    const e = buf[i];
    if (e.type === "segment:play") plays.unshift(e as never);
  }
  const firstT = plays[0]?.t ?? 0;

  // For each play, find its keyHash via the nearest preceding segment:change
  // (which shares its `to` idx + carries the text preview → keyHash proxy
  // via textPreview). Simpler: since debug events store keyHash only on
  // prefetch events, we correlate by walking backwards from each play and
  // matching the most recent prefetch:finish / prefetch:hit whose event
  // timestamp precedes the play.
  const synthMsSamples: number[] = [];
  const gapSamples: number[] = [];
  let cached = 0;
  let cold = 0;

  for (const play of plays) {
    // Walk buffer backwards from just before this play to find the closest
    // ready event, regardless of hash — the player consumes in-order.
    let ready: TtsDebugEvent | null = null;
    for (let i = buf.length - 1; i >= 0; i--) {
      const e = buf[i];
      if (e.t >= play.t) continue;
      if (e.type === "prefetch:finish" || e.type === "prefetch:hit") {
        ready = e;
        break;
      }
    }
    if (play.cached) cached++;
    if (ready) {
      gapSamples.push(play.t - ready.t);
    } else if (!play.cached) {
      cold++;
    }
  }

  // Synth-latency samples: every prefetch:finish inside the play window.
  for (const e of buf) {
    if (e.t < firstT) continue;
    if (e.type === "prefetch:finish") synthMsSamples.push(e.ms);
  }

  const mean = (arr: number[]) =>
    arr.length ? arr.reduce((a, b) => a + b, 0) / arr.length : null;

  return {
    window: plays.length,
    plays: plays.length,
    cachedPlays: cached,
    coldPlays: cold,
    hitRate: plays.length ? cached / plays.length : 0,
    avgSynthMs: mean(synthMsSamples),
    synthSamples: synthMsSamples.length,
    avgReadyGapMs: mean(gapSamples),
    readySamples: gapSamples.length,
  };
}


/**
 * Report a player-side segment event so prefetch/playback timelines
 * correlate. Called from meditation/breathing components — no-op safe if
 * debug is off (still keeps the ring buffer for on-demand inspection).
 */
export function logSegmentEvent(
  evt:
    | { type: "segment:change"; route: string; from: number | null; to: number; text: string }
    | { type: "segment:play"; route: string; idx: number; cached: boolean }
    | { type: "segment:end"; route: string; idx: number; durationMs: number },
) {
  const t = typeof performance !== "undefined" ? performance.now() : Date.now();
  if (evt.type === "segment:change") {
    emitDebug({
      type: "segment:change",
      route: evt.route,
      from: evt.from,
      to: evt.to,
      textPreview: preview(evt.text),
      t,
    });
  } else if (evt.type === "segment:play") {
    emitDebug({ type: "segment:play", route: evt.route, idx: evt.idx, cached: evt.cached, t });
  } else {
    emitDebug({ type: "segment:end", route: evt.route, idx: evt.idx, durationMs: evt.durationMs, t });
  }
}


export class TtsAbortError extends Error {
  constructor() {
    super("tts prefetch aborted");
    this.name = "TtsAbortError";
  }
}

export async function prefetchTts(
  _tts: TtsCaller,
  text: string,
  voiceId: string,
): Promise<string> {
  const key = keyFor(voiceId, text);
  const keyHash = shortHash(key);
  const nowT = () => (typeof performance !== "undefined" ? performance.now() : Date.now());
  const cached = cacheGet(key);
  if (cached) {
    emitDebug({ type: "prefetch:hit", keyHash, textPreview: preview(text), t: nowT() });
    return cached;
  }

  // Attach a fresh waiter for this caller.
  const waiterPromise = new Promise<string>((resolve, reject) => {
    let set = waiters.get(key);
    if (!set) {
      set = new Set();
      waiters.set(key, set);
    }
    set.add({ resolve, reject });
  });

  // Kick off (or reuse) the underlying network fill exactly once per key.
  if (!inflight.has(key)) {
    const startedAt = nowT();
    emitDebug({ type: "prefetch:start", keyHash, textPreview: preview(text), t: startedAt });
    const fill = (async () => {
      try {
        let token: string | null = null;
        try {
          const { supabase } = await import("@/integrations/supabase/client");
          const { data } = await supabase.auth.getSession();
          token = data?.session?.access_token ?? null;
        } catch {
          /* fall back to stored token */
        }
        if (!token) token = getStoredAccessToken();
        // undici (Node/Vitest) rejects relative URLs — resolve against the
        // current origin in the browser, fall back to a localhost base in
        // non-browser test/SSR contexts so fetch always receives an absolute URL.
        const ttsEndpoint =
          typeof window !== "undefined" && window.location?.origin
            ? new URL("/api/tts", window.location.origin).toString()
            : "http://localhost/api/tts";
        const response = await fetch(ttsEndpoint, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          body: JSON.stringify({ text, voiceId, lang: currentVoiceLang() }),
        });
        if (!response.ok) {
          const message = await response.text().catch(() => "");
          throw new Error(message || `voice request failed (${response.status})`);
        }
        const blob = await response.blob();
        if (!blob.size || !blob.type.startsWith("audio/")) {
          throw new Error("no audio returned");
        }
        const url = URL.createObjectURL(blob);
        cacheSet(key, url);
        const elapsed = nowT() - startedAt;
        recordSynthesisTime(elapsed);
        emitDebug({
          type: "prefetch:finish",
          keyHash,
          ms: Math.round(elapsed),
          bytes: url.length,
          t: nowT(),
        });
        waiters.get(key)?.forEach((w) => w.resolve(url));
        return url;
      } catch (err) {
        const elapsed = nowT() - startedAt;
        emitDebug({
          type: "prefetch:error",
          keyHash,
          ms: Math.round(elapsed),
          message: err instanceof Error ? err.message : String(err),
          t: nowT(),
        });
        waiters.get(key)?.forEach((w) => w.reject(err));
        throw err;
      } finally {
        waiters.delete(key);
        inflight.delete(key);
      }
    })();
    inflight.set(key, fill);
    // Suppress unhandled rejection when every waiter has been aborted before
    // the underlying request resolves.
    fill.catch(() => {});
  }

  return waiterPromise;
}

/**
 * Abort every currently-awaited prefetch except those whose keys are listed
 * in `keep`. Underlying network requests continue in the background so the
 * cache still fills for later playback — only the pending awaiters reject
 * with a `TtsAbortError`, so scrub/skip handlers can move on without
 * blocking on the previous segment.
 */
export function abortPendingPrefetches(opts: { keep?: string[] } = {}) {
  const keep = new Set((opts.keep ?? []).map((k) => k));
  const t = typeof performance !== "undefined" ? performance.now() : Date.now();
  for (const [key, set] of waiters) {
    if (keep.has(key)) continue;
    const err = new TtsAbortError();
    set.forEach((w) => w.reject(err));
    waiters.delete(key);
    emitDebug({ type: "prefetch:abort", keyHash: shortHash(key), t });
  }
}

/** Convenience: build the cache key the same way `prefetchTts` does. */
export function ttsKey(text: string, voiceId: string) {
  return keyFor(voiceId, text);
}

export function retryLastTts(tts: TtsCaller) {
  const req = lastRequest;
  if (!req) return;
  // Clear cached failure marker (none stored) and re-run
  if (req.play) {
    speakTts(tts, req.text, req.voiceId, req.opts);
  } else {
    prefetchTts(tts, req.text, req.voiceId).catch((err) => emitError(err));
  }
}

export function cancelTts() {
  if (currentAudio) {
    try {
      currentAudio.pause();
    } catch {}
    currentAudio = null;
  }
  if (typeof window !== "undefined" && "speechSynthesis" in window) {
    try {
      window.speechSynthesis.cancel();
    } catch {}
  }
}

export type PlayOpts = {
  onEnd?: () => void;
  onError?: (err: unknown) => void;
  volume?: number;
  audio?: HTMLAudioElement;
};

export function unlockAudioElement(audio: HTMLAudioElement, volume = 0.95) {
  audio.src = SILENT_WAV_DATA_URL;
  audio.volume = 0;
  void audio.play()
    .then(() => {
      if (audio.src === SILENT_WAV_DATA_URL || audio.currentSrc === SILENT_WAV_DATA_URL) {
        audio.pause();
        audio.currentTime = 0;
      }
      audio.volume = volume;
    })
    .catch(() => {
      audio.volume = volume;
    });
}

export function primeTtsAudio(volume = 0.95): HTMLAudioElement | null {
  if (typeof window === "undefined") return null;
  if (currentAudio) {
    try { currentAudio.pause(); } catch {}
  }
  const audio = new Audio(SILENT_WAV_DATA_URL);
  currentAudio = audio;
  unlockAudioElement(audio, volume);
  return audio;
}

/** Fire-and-forget playback. Cancels any in-progress playback first. */
export function speakTts(
  tts: TtsCaller,
  text: string,
  voiceId: string,
  opts: PlayOpts = {},
) {
  if (typeof window === "undefined") return;
  const audio = opts.audio ?? currentAudio ?? new Audio(SILENT_WAV_DATA_URL);
  if (currentAudio && currentAudio !== audio) {
    try { currentAudio.pause(); } catch {}
  }
  if (typeof window !== "undefined" && "speechSynthesis" in window) {
    try { window.speechSynthesis.cancel(); } catch {}
  }
  currentAudio = audio;
  lastRequest = { text, voiceId, play: true, opts };
  prefetchTts(tts, text, voiceId)
    .then((url) => {
      if (currentAudio !== audio) return;
      audio.src = url;
      audio.volume = opts.volume ?? 0.95;
      audio.onended = () => {
        if (currentAudio === audio) currentAudio = null;
        opts.onEnd?.();
      };
      currentAudio = audio;
      void audio.play().catch((err) => {
        opts.onError?.(err);
        emitError(err);
      });
    })
    .catch((err) => {
      if (err instanceof TtsAbortError) return; // silent — user moved on
      console.error("TTS error", err);
      opts.onError?.(err);
      emitError(err);
    });
}

export function pauseTts() {
  if (currentAudio) {
    try {
      currentAudio.pause();
    } catch {}
  }
}

export function resumeTts() {
  if (currentAudio) {
    void currentAudio.play().catch(() => {});
  }
}

export function getCurrentAudio() {
  return currentAudio;
}

/**
 * Hook that returns TTS helpers bound to a `useServerFn` caller. Use this
 * from within components so the TanStack Start client middleware attaches
 * the Supabase bearer token to each `synthesizeSpeech` RPC.
 */
export function useTts() {
  const tts = useServerFn(synthesizeSpeech);
  return useMemo(
    () => ({
      caller: tts,
      prefetch: (text: string, voiceId: string) => prefetchTts(tts, text, voiceId),
      speak: (text: string, voiceId: string, opts?: PlayOpts) =>
        speakTts(tts, text, voiceId, opts),
      cancel: cancelTts,
      pause: pauseTts,
      resume: resumeTts,
      abortPending: abortPendingPrefetches,
    }),
    [tts],
  );
}
